/**
 * @fileoverview Конвертация чисел в текстовое представление (число прописью)
 * Поддерживает русский (ru) и казахский (kz) языки.
 * Обрабатывает целые числа, дроби (28/1) и десятичные числа.
 */

const RU_ONES = [
  "",
  "один",
  "два",
  "три",
  "четыре",
  "пять",
  "шесть",
  "семь",
  "восемь",
  "девять",
];

const RU_ONES_FEM = [
  "",
  "одна",
  "две",
  "три",
  "четыре",
  "пять",
  "шесть",
  "семь",
  "восемь",
  "девять",
];

const RU_TEENS = [
  "десять",
  "одиннадцать",
  "двенадцать",
  "тринадцать",
  "четырнадцать",
  "пятнадцать",
  "шестнадцать",
  "семнадцать",
  "восемнадцать",
  "девятнадцать",
];

const RU_TENS = [
  "",
  "",
  "двадцать",
  "тридцать",
  "сорок",
  "пятьдесят",
  "шестьдесят",
  "семьдесят",
  "восемьдесят",
  "девяносто",
];

const RU_HUNDREDS = [
  "",
  "сто",
  "двести",
  "триста",
  "четыреста",
  "пятьсот",
  "шестьсот",
  "семьсот",
  "восемьсот",
  "девятьсот",
];

const RU_ORDERS = [
  { forms: ["", "", ""], feminine: false },
  { forms: ["тысяча", "тысячи", "тысяч"], feminine: true },
  { forms: ["миллион", "миллиона", "миллионов"], feminine: false },
  { forms: ["миллиард", "миллиарда", "миллиардов"], feminine: false },
  { forms: ["триллион", "триллиона", "триллионов"], feminine: false },
];

function ruPluralForm(n, forms) {
  const abs = Math.abs(n) % 100;
  if (abs >= 11 && abs <= 19) return forms[2];
  const last = abs % 10;
  if (last === 1) return forms[0];
  if (last >= 2 && last <= 4) return forms[1];
  return forms[2];
}

function ruHundreds(n, feminine) {
  const parts = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;

  if (h > 0) parts.push(RU_HUNDREDS[h]);

  if (rest >= 10 && rest <= 19) {
    parts.push(RU_TEENS[rest - 10]);
  } else {
    const t = Math.floor(rest / 10);
    const o = rest % 10;
    if (t > 0) parts.push(RU_TENS[t]);
    if (o > 0) parts.push(feminine ? RU_ONES_FEM[o] : RU_ONES[o]);
  }

  return parts.join(" ");
}

function numberToWordsRu(num) {
  if (num === 0) return "ноль";

  const isNegative = num < 0;
  num = Math.abs(num);

  const parts = [];
  let orderIndex = 0;

  while (num > 0) {
    const chunk = num % 1000;
    if (chunk > 0) {
      const order = RU_ORDERS[orderIndex];
      const feminine = order?.feminine || false;
      const chunkText = ruHundreds(chunk, feminine);
      const orderText = order?.forms ? ruPluralForm(chunk, order.forms) : "";

      parts.unshift((chunkText + " " + orderText).trim());
    }
    num = Math.floor(num / 1000);
    orderIndex++;
  }

  const result = parts.join(" ");
  return isNegative ? "минус " + result : result;
}

const KZ_ONES = [
  "",
  "бір",
  "екі",
  "үш",
  "төрт",
  "бес",
  "алты",
  "жеті",
  "сегіз",
  "тоғыз",
];

const KZ_TENS = [
  "",
  "он",
  "жиырма",
  "отыз",
  "қырық",
  "елу",
  "алпыс",
  "жетпіс",
  "сексен",
  "тоқсан",
];

const KZ_HUNDREDS = [
  "",
  "жүз",
  "екі жүз",
  "үш жүз",
  "төрт жүз",
  "бес жүз",
  "алты жүз",
  "жеті жүз",
  "сегіз жүз",
  "тоғыз жүз",
];

const KZ_ORDERS = ["", "мың", "миллион", "миллиард", "триллион"];

function kzHundreds(n) {
  const parts = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;

  if (h > 0) parts.push(KZ_HUNDREDS[h]);

  const t = Math.floor(rest / 10);
  const o = rest % 10;
  if (t > 0) parts.push(KZ_TENS[t]);
  if (o > 0) parts.push(KZ_ONES[o]);

  return parts.join(" ");
}

function numberToWordsKz(num) {
  if (num === 0) return "нөл";

  const isNegative = num < 0;
  num = Math.abs(num);

  const parts = [];
  let orderIndex = 0;

  while (num > 0) {
    const chunk = num % 1000;
    if (chunk > 0) {
      const chunkText = kzHundreds(chunk);
      const orderText = KZ_ORDERS[orderIndex] || "";
      parts.unshift((chunkText + " " + orderText).trim());
    }
    num = Math.floor(num / 1000);
    orderIndex++;
  }

  const result = parts.join(" ");
  return isNegative ? "минус " + result : result;
}

// ==================== ОБЩИЙ ИНТЕРФЕЙС ====================

/**
 * Парсит строковое значение и определяет тип: целое, дробь (/) или десятичное
 * @param {string|number} value
 * @returns {{ type: 'integer'|'fraction'|'decimal', integer?: number, numerator?: number, denominator?: number, intPart?: number, decPart?: string }}
 */
function parseNumericValue(value) {
  const str = String(value).trim().replace(/\s/g, "");

  // Дробь вида "28/1"
  if (str.includes("/")) {
    const [num, den] = str.split("/").map((s) => parseInt(s, 10));
    if (!isNaN(num) && !isNaN(den)) {
      return { type: "fraction", numerator: num, denominator: den };
    }
  }

  // Десятичное число
  if (str.includes(".") || str.includes(",")) {
    const normalized = str.replace(",", ".");
    const num = parseFloat(normalized);
    if (!isNaN(num)) {
      const [intStr, decStr] = normalized.split(".");
      return {
        type: "decimal",
        intPart: parseInt(intStr, 10) || 0,
        decPart: decStr || "0",
      };
    }
  }

  // Целое число
  const num = parseInt(str, 10);
  if (!isNaN(num)) {
    return { type: "integer", integer: num };
  }

  return null;
}

/**
 * Конвертирует число в текстовое представление
 * @param {string|number} value - Числовое значение (целое, "28/1", "10000.50")
 * @param {string} lang - Язык: "ru" | "kz"
 * @returns {string} Число прописью
 */
export function numberToWords(value, lang = "ru") {
  if (value === null || value === undefined || value === "") return "";

  const parsed = parseNumericValue(value);
  if (!parsed) return String(value);

  const convert = lang === "kz" ? numberToWordsKz : numberToWordsRu;
  const fractionWord = lang === "kz" ? "бөлшек" : "дробь";
  const pointWord = lang === "kz" ? "бүтін" : "целых";

  switch (parsed.type) {
    case "integer":
      return convert(parsed.integer);

    case "fraction":
      return `${convert(parsed.numerator)} ${fractionWord} ${convert(
        parsed.denominator
      )}`;

    case "decimal": {
      const intText = convert(parsed.intPart);
      // Конвертируем дробную часть как целое число
      const decNum = parseInt(parsed.decPart, 10);
      if (decNum === 0) return intText;
      const decText = convert(decNum);
      return `${intText} ${pointWord} ${decText}`;
    }

    default:
      return String(value);
  }
}

/**
 * Извлекает базовый ключ из calc(key)
 * @param {string} calcKey - Ключ вида "calc(summary)" или "calc(gbdfl_amount_supruga)"
 * @returns {string|null} Базовый ключ или null
 */
export function extractBaseKeyFromCalc(calcKey) {
  if (!calcKey) return null;
  const match = calcKey.match(/^calc\((.+)\)$/);
  return match ? match[1] : null;
}
